export interface Option {
  id: string
  label: string
}

// Legacy Question type for backward compatibility
export interface LegacyQuestion {
  field_id: string
  label: string
  displayLabel?: string | null
  type: string
  subtype: string
  mandatory: boolean
  placeholder: string
  order: number
  enabled?: boolean
  isReserved?: boolean
  description?: string
  // Optional properties for different question types
  options?: Option[]
  min?: number
  max?: number
  max_char?: number
  validate_schedule_dates?: boolean
  usa_hockey_validation_type?: 'none' | 'full' | 'partial'
}

interface BaseQuestion {
  field_id: string
  label: string
  // Reserved fields (e.g. Player Email) keep their canonical `label`/name for
  // internal matching while exposing a customizable visible label here.
  displayLabel?: string | null
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'dropdown'
    | 'checkbox'
    | 'date'
    | 'birthdate'
    | 'phone'
    | 'email'
    | 'url'
    | 'country'
    | 'player_position'
    | 'hockey_usa'
    | 'player_number'
  subtype: string
  mandatory: boolean
  placeholder: string
  order: number
  enabled?: boolean
  isReserved?: boolean
  description?: string
}

interface HockeyUSAQuestion extends BaseQuestion {
  type: 'hockey_usa'
  validate_schedule_dates: boolean
  usa_hockey_validation_type: 'none' | 'full' | 'partial'
}

interface ChoiceQuestion extends BaseQuestion {
  type: 'dropdown' // Only dropdown has options
  options: Option[]
  shuffleOptions?: boolean
}

interface CheckboxQuestion extends BaseQuestion {
  type: 'checkbox' // Checkbox is just a boolean true/false
}

interface NumberQuestion extends BaseQuestion {
  type: 'number'
  min?: number
  max?: number
}

interface PlayerNumberQuestion extends BaseQuestion {
  type: 'player_number'
  // Player number fields don't have min/max constraints
}

interface TextQuestion extends BaseQuestion {
  type:
    | 'text'
    | 'textarea'
    | 'date'
    | 'birthdate'
    | 'phone'
    | 'email'
    | 'url'
    | 'country'
    | 'player_position'
    | 'hockey_usa'
  max_char?: number
  validate_schedule_dates?: boolean
}

export type Question =
  | TextQuestion
  | NumberQuestion
  | PlayerNumberQuestion
  | CheckboxQuestion
  | HockeyUSAQuestion
  | (ChoiceQuestion & {
      is_reserved?: boolean
    })

// Type guards that work with both legacy and new questions
export const isHockeyUSAQuestion = (
  question: Question | LegacyQuestion
): question is HockeyUSAQuestion => {
  return question.type === 'hockey_usa'
}

export const isChoiceQuestion = (
  question: Question | LegacyQuestion
): question is ChoiceQuestion => {
  return question.type === 'dropdown' // Only dropdown is a choice question now
}

export const isCheckboxQuestion = (
  question: Question | LegacyQuestion
): question is CheckboxQuestion => {
  return question.type === 'checkbox'
}

export const isNumberQuestion = (
  question: Question | LegacyQuestion
): question is NumberQuestion => {
  return question.type === 'number'
}

export const isPlayerNumberQuestion = (
  question: Question | LegacyQuestion
): question is PlayerNumberQuestion => {
  return question.type === 'player_number'
}

export const isTextQuestion = (
  question: Question | LegacyQuestion
): question is TextQuestion => {
  return [
    'text',
    'textarea',
    'date',
    'birthdate',
    'phone',
    'email',
    'url',
    'country',
    'player_position',
    'hockey_usa',
  ].includes(question.type)
}

// Helper function to generate unique IDs
export const generateId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Conversion functions for backward compatibility
export const convertLegacyToNewQuestion = (
  legacyQuestion: LegacyQuestion
): Question => {
  const baseQuestion = {
    field_id: legacyQuestion.field_id,
    label: legacyQuestion.label,
    displayLabel: legacyQuestion.displayLabel,
    type: legacyQuestion.type as Question['type'],
    subtype: legacyQuestion.subtype,
    mandatory: legacyQuestion.mandatory,
    placeholder: legacyQuestion.placeholder,
    order: legacyQuestion.order,
    enabled: legacyQuestion.enabled,
    isReserved: legacyQuestion.isReserved,
    description: legacyQuestion.description,
  }

  if (isHockeyUSAQuestion(legacyQuestion)) {
    return {
      ...baseQuestion,
      validate_schedule_dates: legacyQuestion.validate_schedule_dates || false,
      usa_hockey_validation_type:
        legacyQuestion.usa_hockey_validation_type || 'none',
    } as HockeyUSAQuestion
  }

  if (isChoiceQuestion(baseQuestion)) {
    return {
      ...baseQuestion,
      options: legacyQuestion.options || [], // Use legacy options or default empty
    } as ChoiceQuestion
  } else if (isNumberQuestion(baseQuestion)) {
    return {
      ...baseQuestion,
      min: legacyQuestion.min,
      max: legacyQuestion.max,
    } as NumberQuestion
  } else if (isPlayerNumberQuestion(baseQuestion)) {
    return baseQuestion as PlayerNumberQuestion
  } else if (isCheckboxQuestion(baseQuestion)) {
    return baseQuestion as CheckboxQuestion
  } else {
    return {
      ...baseQuestion,
      max_char: legacyQuestion.max_char,
    } as TextQuestion
  }
}

export const convertNewToLegacyQuestion = (
  question: Question
): LegacyQuestion => {
  const baseQuestion: LegacyQuestion = {
    field_id: question.field_id,
    label: question.label,
    displayLabel: question.displayLabel,
    type: question.type,
    subtype: question.subtype,
    mandatory: question.mandatory,
    placeholder: question.placeholder,
    order: question.order,
    enabled: question.enabled,
    isReserved: question.isReserved,
    description: question.description,
  }

  if (isHockeyUSAQuestion(question)) {
    baseQuestion.validate_schedule_dates = question.validate_schedule_dates
    baseQuestion.usa_hockey_validation_type =
      question.usa_hockey_validation_type
  } else if (isChoiceQuestion(question)) {
    baseQuestion.options = question.options
  } else if (isNumberQuestion(question)) {
    baseQuestion.min = question.min
    baseQuestion.max = question.max
  } else if (isPlayerNumberQuestion(question)) {
    // No additional properties for player_number (no min/max)
  } else if (isCheckboxQuestion(question)) {
    // No additional properties for checkbox
  } else if (isTextQuestion(question)) {
    baseQuestion.max_char = (question as TextQuestion).max_char
  }

  return baseQuestion
}

// Question form draft type - represents the form state during creation/editing
export interface QuestionFormDraft {
  label: string
  displayLabel?: string
  type: Question['type']
  placeholder: string
  description: string
  mandatory: boolean
  options: Option[]
  min: string
  max: string
  max_char: string
  validate_schedule_dates: boolean
  usa_hockey_validation_type: 'none' | 'full' | 'partial'
}
